import {connect} from 'pwa-helpers';
import {LitElement, html} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {msg} from '@lit/localize';
import {navigator, router} from 'lit-element-router';
import deepEqual from 'deep-equal';
import store from '../../store';

import style from '../../../../scss/profileList.scss';
import listStyle from '../../../../scss/list.scss';

import {
	deselectProfiles,
	fetchProfiles,
	massUpdateProfiles,
	selectAllProfiles,
	selectedProfiles,
	selectProfiles,
} from '../../slices/profileSlice';
import {selectCurrentAccountId, selectCurrentProfileId, selectLanguage} from '../../selectors';
import getRoutes from '../../routes';

@customElement('arc-profiles-list')
export class ProfilesList extends connect(store)(router(navigator(LitElement))) {
	static styles = [listStyle, style];

	static properties = {
		selectMultiple: {type: Boolean},
		contextClass: {type: String},
		selectedChange: {type: Object},
		selected: {type: Boolean},
		updateNeeded: {type: Boolean},
	};

	constructor() {
		super();
		this.selected = false;
		this.selectedChange = {
			isReadOnly: '',
		};
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.lang = selectLanguage(state);
		this.profiles = selectAllProfiles(state);
		this.profileId = selectCurrentProfileId(state);
		this.accountId = selectCurrentAccountId(state);
		this.totalPages = state.profile.profilePagination.pagesAmount;
	}

	static get routes() {
		return getRoutes();
	}

	router(route, params, query, data) {
		// check if we need to update the series list
		this.updateNeeded =
			route === 'account-profiles' && (!deepEqual(this.query, query));

		this.params = params;
		this.query = query;
	}
	connectedCallback() {
		super.connectedCallback();
		store.dispatch(fetchProfiles({isOwned: -1, ...this.query}));
	}

	disconnectedCallback() {
		super.disconnectedCallback();
	}

	_refreshProfilesList() {
		store.dispatch(fetchProfiles({isOwned: -1, ...this.query}));
	}

	selectAllProfiles(checked) {
		const profileIds = this.profiles.map((profile) => profile.id);
		if (checked) {
			store.dispatch(selectProfiles({profileIds: profileIds}));
		} else {
			store.dispatch(deselectProfiles({profileIds: profileIds}));
		}
	}

	getIsReadOnlyOptions() {
		const isReadOnlyOptions = [
			{id: '0', name: 'No'},
			{id: '1', name: 'Yes'},
		];
		return [{id: '', name: ''}, ...isReadOnlyOptions];
	}
	selectedOnThisPage() {
		return this.profiles.reduce(
			(acc, profile) => (selectedProfiles(this.state).includes(profile.id) ? acc + 1 : acc),
			0
		);
	}

	someSelected() {
		return this.selectedOnThisPage() > 0;
	}

	allSelected() {
		return this.state.profile.profilePagination.amount === this.selectedOnThisPage();
	}

	isIndeterminate() {
		return this.someSelected() && !this.allSelected();
	}

	onEditProfiles() {
		this.dialog = this.renderRoot.querySelector('#editProfilesDialog');
		this.dialog.openDialog();
		this._handleConfirm = async () => {
			const options = {
				profileIds: selectedProfiles(this.state),
				isReadOnly: this.selectedChange.isReadOnly,
			};
			const result = await store.dispatch(massUpdateProfiles(options));
			if (result.meta.requestStatus === 'fulfilled') {
				this._refreshProfilesList();
				this.selectedChange = {
					isReadOnly: '',
				};
			}
			window.removeEventListener('confirm', this._handleConfirm);
		};
		window.addEventListener('confirm', this._handleConfirm);
	}

	editSelectedChange(field, value) {
		this.selectedChange[field] = value;
	}

	render() {
		const state = store.getState();
		return html`
			<div class="list profilesList">
				<div class="listHeader">
					<div class="selectAllCheckbox">
						${when(
							this.selectMultiple,
							() => html`
								<arc-checkbox
									class="formInput checkbox"
									title=""
									.noFieldTitle=${true}
									title=${msg('')}
									.value=${this.someSelected() ? 1 : 0}
									.showIcon=${false}
									.required=${false}
									.changeIndication=${false}
									.indeterminate=${this.isIndeterminate()}
									.tooltip="${false}"
									@value-change=${(e) => this.selectAllProfiles(e.detail.value)}
								>
								</arc-checkbox>
							`
						)}
					</div>
					<div>${msg('Id')}</div>
					<div>${msg('Profile Name')}</div>
					<div>${msg('Owner')}</div>
					<div>${msg('Read Only')}</div>
					<div>${msg('Last Modified on')}</div>
					<div>${msg('Profile Permissions')}</div>
					<div>${msg('Artwork Permissions')}</div>
				</div>

				${repeat(
					this.profiles,
					(profile) => profile.id,
					(profile) => html`
						<arc-profiles-list-item .selectMultiple=${this.selectMultiple} .profile=${profile}>
						</arc-profiles-list-item>
					`
				)}

				<div class="profileListFooter nosort">
					${when(
						this.selectMultiple,
						() => html`
							<arc-button
								@click=${this.onEditProfiles}
								.disabled=${selectedProfiles(this.state).length === 0}
								title="${msg('Edit Selected')} (${selectedProfiles(this.state).length})"
								type="secondary"
								.additionalInfo="${false}"
							>
							</arc-button>
						`
					)}
					<div class="paginationContainer">
						<arc-pagination .totalPages=${this.totalPages}></arc-pagination>
					</div>
				</div>
			</div>

			<arc-request-dialog
				id="editProfilesDialog"
				title="${msg('Edit Profiles')}"
				message="${msg('Edit all selected Profiles')}"
			>
				<div class="editProfilesDialogContent">
					<arc-select
						class="formInput"
						.multiple=${false}
						.title="${msg('Read Only')}"
						.edit=${true}
						.changeIndication=${false}
						.options=${this.getIsReadOnlyOptions()}
						.value=${this.selectedChange.isReadOnly}
						@value-change=${(e) => this.editSelectedChange('isReadOnly', e.detail.value)}
					></arc-select>
				</div>
			</arc-request-dialog>
		`;
	}
}
